import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';

interface PopoverProps {
  className?: string;
  portalElement?: HTMLElement;
  deps: unknown;
  children: ReactNode;
}

interface PopoverContextType {
  ref: HTMLDivElement | null;
}

const PopoverContext = createContext({} as PopoverContextType);

export const PopoverProvider = ({ children }: { children: ReactNode }) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  return (
    <PopoverContext.Provider value={{ ref }}>
      {children}
      <div className="popover-container" ref={setRef}></div>
    </PopoverContext.Provider>
  );
};

export const Popover: FC<PopoverProps> = ({
  children,
  className = '',
  portalElement,
  deps
}) => {
  const { ref: containerRef } = useContext(PopoverContext);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const docPos = {
      top: rect.top + window.pageYOffset,
      left: rect.left + window.pageXOffset
    };
    const { top, left } = docPos;
    setPosition({ x: left, y: top });
  }, [ref, setPosition, deps]);

  const elementToRenderTo = portalElement ?? containerRef;

  return (
    <>
      {position &&
        elementToRenderTo &&
        ref &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: position.y,
              left: position.x,
              visibility: position ? 'visible' : 'hidden'
            }}
          >
            {children}
          </div>,
          elementToRenderTo
        )}

      <div
        className={`popover-container ${className}`}
        ref={ref}
        style={{ visibility: position ? 'hidden' : 'visible' }}
      >
        {children}
      </div>
    </>
  );
};
